import React, { useState, useEffect } from 'react';

const AchievementNotification = ({ achievement, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    if (achievement) {
      setIsVisible(true);
      
      // Auto-hide after 7 seconds (longer for better visibility)
      const timer = setTimeout(() => {
        handleClose();
      }, 7000);

      return () => clearTimeout(timer);
    }
  }, [achievement]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsLeaving(false);
      onClose();
    }, 300);
  };

  const getCategoryColor = (category) => {
    const colors = {
      milestone: 'from-purple-400 to-purple-600',
      creative: 'from-blue-400 to-blue-600',
      engagement: 'from-green-400 to-green-600',
      social: 'from-orange-400 to-orange-600',
      streak: 'from-red-400 to-red-600',
    };
    return colors[category] || 'from-gray-400 to-gray-600';
  };

  // Generate motivational message based on achievement type
  const getMotivationalMessage = (achievement) => {
    if (!achievement || !achievement.definition) return "Keep going! 🚀";
    
    const { name, category } = achievement.definition;
    
    const motivationalMessages = {
      milestone: [
        "You're building momentum! 🔥",
        "Every milestone matters! 🎯", 
        "Great progress! Keep it up! 💪",
        "Your dedication is paying off! ⭐"
      ],
      creative: [
        "Your creativity is shining! ✨",
        "Innovation at its finest! 🎨",
        "Creative genius at work! 🧠",
        "Art meets marketing! 🎭"
      ],
      engagement: [
        "Consistency is key! 📈",
        "Building great habits! 🌱",
        "Your engagement is inspiring! 💫",
        "Active and amazing! ⚡"
      ],
      social: [
        "Spreading the magic! 🌍",
        "Your network is your net worth! 🤝",
        "Sharing is caring! 💝",
        "Building connections! 🌐"
      ],
      streak: [
        "On fire! 🔥",
        "Unstoppable momentum! 🚀",
        "Streak master! ⚡",
        "Consistency champion! 🏆"
      ]
    };
    
    const categoryMessages = motivationalMessages[category] || motivationalMessages.milestone;
    return categoryMessages[Math.floor(Math.random() * categoryMessages.length)];
  };

  if (!achievement || !isVisible) return null;

  return (
    <div className="fixed top-4 right-4 z-50">
      <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-2xl border-2 border-orange-200 dark:border-orange-700 p-6 max-w-sm transform transition-all duration-300 ${
        isLeaving ? 'translate-x-full opacity-0' : 'translate-x-0 opacity-100'
      } hover:scale-105`}>
        {/* Subtle glow effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-orange-100 to-yellow-100 dark:from-orange-900/20 dark:to-yellow-900/20 rounded-xl opacity-20"></div>
        
        {/* Header */}
        <div className="relative flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <span className="text-2xl animate-bounce">🎉</span>
            <span className="text-lg font-bold text-orange-500">Achievement Unlocked!</span>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors hover:rotate-90 transform duration-200"
          >
            ✕
          </button>
        </div>

        {/* Achievement Content */}
        <div className="relative flex items-center space-x-4">
          {/* Animated Badge */}
          <div className={`relative w-16 h-16 rounded-full flex items-center justify-center text-2xl bg-gradient-to-br ${getCategoryColor(achievement.definition?.category)} text-white shadow-lg animate-pulse`}>
            {achievement.definition?.badge_icon || '🏆'}
            
            {/* Sparkle effect */}
            <div className="absolute -top-1 -right-1 text-yellow-400 text-sm animate-ping">✨</div>
          </div>

          {/* Details */}
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
              {achievement.definition?.name}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
              {achievement.definition?.description}
            </p>
            
            {/* Motivational message */}
            <p className="text-xs font-medium text-orange-600 dark:text-orange-400 mb-2 italic">
              {getMotivationalMessage(achievement)}
            </p>
            
            <div className="flex items-center justify-between">
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                achievement.definition?.category === 'milestone' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' :
                achievement.definition?.category === 'creative' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                achievement.definition?.category === 'engagement' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                achievement.definition?.category === 'social' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' :
                achievement.definition?.category === 'streak' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
              }`}>
                {achievement.definition?.category}
              </span>
              <span className="text-sm font-bold text-orange-500 flex items-center">
                +{achievement.definition?.points} 
                <span className="ml-1 text-xs">points</span>
              </span>
            </div>
          </div>
        </div>

        {/* Progress indicator */}
        <div className="relative mt-4">
          <div className="flex justify-center">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Keep creating amazing campaigns! 🌟
            </div>
          </div>
        </div>

        {/* Celebration particles */}
        <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full animate-bounce flex items-center justify-center shadow-lg">
          <span className="text-sm">🌟</span>
        </div>
      </div>
    </div>
  );
};

export default AchievementNotification;